'use client'

import { useAuthorization } from '@entities/authentication'
import { env } from '@shared/config/env'
import { FEATURE_FLAG_KEYS } from '@shared/constants'
import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useFlagsCompat } from '@podo/flags/react'

import { OnetalkWidgetStatusProvider, type OnetalkWidgetStatus } from './onetalk-widget.context'

type IdentityResponse = {
  memberSessionToken: string
  expiresAt: string
}

type LoadedIdentity = IdentityResponse & {
  generation: number
  authUserId: number
}

const ONETALK_WIDGET_HOST_ID = 'onetalk-widget'
const ONETALK_DEFAULT_LAUNCHER_STYLE_ID = 'podo-onetalk-default-launcher-style'

export const OnetalkWidgetProvider = ({ children }: { children: React.ReactNode }) => {
  const baseUrl = env.NEXT_PUBLIC_ONETALK_BASE_URL
  const channelApiKey = env.NEXT_PUBLIC_ONETALK_CHANNEL_API_KEY
  const pathname = usePathname()
  const isTrialHandoffPage = /^\/lessons\/classroom\/\d+\/onetalk$/.test(pathname)

  const authState = useAuthorization()
  const { [FEATURE_FLAG_KEYS.ONETALK_WIDGET_ENABLED]: onetalkWidgetFlag } = useFlagsCompat([
    FEATURE_FLAG_KEYS.ONETALK_WIDGET_ENABLED,
  ])
  const authUserId = authState.isAuthorized ? authState.uid : null
  const [identity, setIdentity] = useState<LoadedIdentity | null>(null)
  const [identityRequestVersion, setIdentityRequestVersion] = useState(0)
  const [status, setStatus] = useState<OnetalkWidgetStatus>('loading')
  const [isWidgetRequested, setIsWidgetRequested] = useState(false)
  const isWidgetReadyRef = useRef(false)
  const isWidgetOpenRef = useRef(false)
  const widgetShadowRootRef = useRef<ShadowRoot | null>(null)
  const removeWidgetCloseListenerRef = useRef<() => void>(() => undefined)
  const shouldOpenWhenReadyRef = useRef(false)
  const identityGenerationRef = useRef(0)
  const identityRetryCountRef = useRef(0)
  const lastAuthUserIdRef = useRef<number | null>(authUserId)
  const isWidgetEnabled = onetalkWidgetFlag.enabled || isWidgetRequested

  const retryIdentityOnce = useCallback(() => {
    if (identityRetryCountRef.current >= 1) {
      return false
    }
    identityRetryCountRef.current += 1
    setIdentityRequestVersion((version) => version + 1)
    return true
  }, [])

  const resetWidgetRuntime = useCallback((options: { preserveOpenIntent?: boolean } = {}) => {
    identityGenerationRef.current += 1
    if (!options.preserveOpenIntent) {
      shouldOpenWhenReadyRef.current = false
    }
    isWidgetReadyRef.current = false
    isWidgetOpenRef.current = false
    removeWidgetCloseListenerRef.current()
    widgetShadowRootRef.current = null
    window.Onetalk?.reset?.()
    window.Onetalk?.destroy?.()
    document.getElementById(ONETALK_WIDGET_HOST_ID)?.remove()
  }, [])

  const syncDefaultLauncherVisibility = useCallback(
    (shadowRoot = widgetShadowRootRef.current) => {
      if (!shadowRoot) {
        return
      }

      const existingStyle = shadowRoot.getElementById(ONETALK_DEFAULT_LAUNCHER_STYLE_ID)
      if (onetalkWidgetFlag.enabled) {
        existingStyle?.remove()
        return
      }

      if (!existingStyle) {
        const style = document.createElement('style')
        style.id = ONETALK_DEFAULT_LAUNCHER_STYLE_ID
        style.textContent = '.onetalk-launcher-wrapper { display: none !important; }'
        shadowRoot.appendChild(style)
      }
    },
    [onetalkWidgetFlag.enabled],
  )

  const captureWidgetShadowRoot = useCallback(() => {
    const originalAttachShadow = Element.prototype.attachShadow

    // SDK가 닫힌 Shadow DOM과 기본 런처를 함께 만들기 때문에 동기 init 구간에서만 루트를 확보한다.
    Element.prototype.attachShadow = function (init) {
      const shadowRoot = originalAttachShadow.call(this, init)
      if (this.id === ONETALK_WIDGET_HOST_ID) {
        removeWidgetCloseListenerRef.current()
        widgetShadowRootRef.current = shadowRoot
        syncDefaultLauncherVisibility(shadowRoot)

        const handleWidgetClose = (event: Event) => {
          const target = event.target
          if (
            !shadowRoot.getElementById(ONETALK_DEFAULT_LAUNCHER_STYLE_ID) ||
            !(target instanceof Element) ||
            !target.closest('.onetalk-header-close')
          ) {
            return
          }

          isWidgetOpenRef.current = false
          const widgetHost = document.getElementById(ONETALK_WIDGET_HOST_ID)
          if (widgetHost) {
            widgetHost.style.display = 'none'
          }
        }

        shadowRoot.addEventListener('click', handleWidgetClose)
        removeWidgetCloseListenerRef.current = () => {
          shadowRoot.removeEventListener('click', handleWidgetClose)
        }
      }
      return shadowRoot
    }

    return () => {
      Element.prototype.attachShadow = originalAttachShadow
    }
  }, [syncDefaultLauncherVisibility])

  const syncWidgetHostVisibility = useCallback(
    (isOpen = isWidgetOpenRef.current) => {
      const widgetHost = document.getElementById(ONETALK_WIDGET_HOST_ID)
      if (!widgetHost) {
        return
      }

      // SDK 준비 전과 상담 호출 실패 시 위젯 전체가 노출되지 않도록 호스트 가시성도 함께 제어한다.
      widgetHost.style.display = onetalkWidgetFlag.enabled || isOpen ? '' : 'none'
    },
    [onetalkWidgetFlag.enabled],
  )

  const openReadyWidget = useCallback(() => {
    const openOnetalk = window.Onetalk?.open?.bind(window.Onetalk)
    if (!isWidgetReadyRef.current || !openOnetalk) {
      return false
    }

    shouldOpenWhenReadyRef.current = false
    isWidgetOpenRef.current = true
    syncWidgetHostVisibility(true)
    try {
      void Promise.resolve(openOnetalk()).catch(() => {
        isWidgetOpenRef.current = false
        syncWidgetHostVisibility(false)
      })
    } catch {
      // 원톡 장애가 페이지 이동이나 앱 동작에 영향을 주지 않도록 호출 실패만 무시한다.
      isWidgetOpenRef.current = false
      syncWidgetHostVisibility(false)
    }
    return true
  }, [syncWidgetHostVisibility])

  const prepareWidget = useCallback(() => {
    setIsWidgetRequested(true)
  }, [])

  const openWidget = useCallback(() => {
    if (openReadyWidget()) {
      return
    }

    shouldOpenWhenReadyRef.current = true
    setIsWidgetRequested(true)
  }, [openReadyWidget])

  useEffect(() => {
    const handleWidgetClose = () => {
      isWidgetOpenRef.current = false
      syncWidgetHostVisibility(false)
    }

    window.addEventListener('popstate', handleWidgetClose)
    return () => window.removeEventListener('popstate', handleWidgetClose)
  }, [syncWidgetHostVisibility])

  useEffect(() => {
    if (isWidgetReadyRef.current) {
      syncWidgetHostVisibility()
    }
  }, [syncWidgetHostVisibility])

  useEffect(() => {
    syncDefaultLauncherVisibility()
  }, [syncDefaultLauncherVisibility])

  useLayoutEffect(() => () => resetWidgetRuntime(), [resetWidgetRuntime])

  useLayoutEffect(() => {
    if (lastAuthUserIdRef.current === authUserId) {
      return
    }
    identityRetryCountRef.current = 0
    resetWidgetRuntime()
    lastAuthUserIdRef.current = authUserId
  }, [authUserId, resetWidgetRuntime])

  useEffect(() => {
    const preserveOpenIntent = lastAuthUserIdRef.current === authUserId && shouldOpenWhenReadyRef.current
    resetWidgetRuntime({ preserveOpenIntent })
    const generation = identityGenerationRef.current
    setIdentity(null)

    if (isTrialHandoffPage || !isWidgetEnabled || !baseUrl || !channelApiKey || authUserId === null) {
      setStatus('unavailable')
      return
    }

    const controller = new AbortController()
    setStatus('loading')

    fetch('/api/onetalk/identity', {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status >= 500) {
            throw new Error(`Onetalk identity request failed: ${res.status}`)
          }
          return null
        }
        return res.json() as Promise<IdentityResponse>
      })
      .then((data) => {
        if (generation !== identityGenerationRef.current) {
          return
        }
        if (
          data &&
          typeof data.memberSessionToken === 'string' &&
          data.memberSessionToken.length >= 40 &&
          typeof data.expiresAt === 'string'
        ) {
          setIdentity({ ...data, generation, authUserId })
        } else {
          setStatus('unavailable')
        }
      })
      .catch(() => {
        if (controller.signal.aborted || generation !== identityGenerationRef.current) {
          return
        }
        // 상담 위젯 장애가 본 서비스에 영향을 주지 않도록 조용히 무시
        const willRetry = retryIdentityOnce()
        resetWidgetRuntime({ preserveOpenIntent: willRetry })
        setStatus(willRetry ? 'loading' : 'unavailable')
      })

    return () => {
      controller.abort()
    }
  }, [
    authUserId,
    baseUrl,
    channelApiKey,
    identityRequestVersion,
    isTrialHandoffPage,
    isWidgetEnabled,
    resetWidgetRuntime,
    retryIdentityOnce,
  ])

  const isWidgetEligible = !(
    isTrialHandoffPage ||
    !isWidgetEnabled ||
    !baseUrl ||
    !channelApiKey ||
    !authState.isAuthorized ||
    !identity ||
    identity.authUserId !== authUserId
  )
  const contextValue = useMemo(
    () => ({ status, open: openWidget, prepare: prepareWidget }),
    [openWidget, prepareWidget, status],
  )

  return (
    <OnetalkWidgetStatusProvider value={contextValue}>
      {children}
      {isWidgetEligible ? (
        <Script
          key={`onetalk-${identity.authUserId}-${identity.generation}`}
          src={`${baseUrl}/widget/sdk-v2.js`}
          strategy="lazyOnload"
          onError={() => {
            if (identity.generation !== identityGenerationRef.current) {
              return
            }
            resetWidgetRuntime()
            setIdentity(null)
            setStatus('unavailable')
          }}
          onReady={() => {
            const generation = identity.generation
            if (
              generation !== identityGenerationRef.current ||
              identity.authUserId !== authUserId ||
              window.Onetalk?.identityContractVersion !== 2
            ) {
              resetWidgetRuntime()
              setIdentity(null)
              setStatus('unavailable')
              return
            }
            const restoreAttachShadow = captureWidgetShadowRoot()
            let initialize: Promise<void> | undefined
            try {
              initialize = window.Onetalk?.init({
                channelApiKey,
                apiBaseUrl: baseUrl,
                memberSessionToken: identity.memberSessionToken,
              })
            } catch {
              const willRetry = retryIdentityOnce()
              resetWidgetRuntime({ preserveOpenIntent: willRetry })
              setIdentity(null)
              setStatus(willRetry ? 'loading' : 'unavailable')
              return
            } finally {
              restoreAttachShadow()
            }
            syncWidgetHostVisibility(false)
            if (!initialize) {
              resetWidgetRuntime()
              setIdentity(null)
              setStatus('unavailable')
              return
            }
            void initialize
              .then(() => {
                if (generation !== identityGenerationRef.current) {
                  return
                }
                identityRetryCountRef.current = 0
                isWidgetReadyRef.current = true
                setStatus('ready')
                if (shouldOpenWhenReadyRef.current) {
                  openReadyWidget()
                } else {
                  syncWidgetHostVisibility(false)
                }
              })
              .catch(() => {
                if (generation !== identityGenerationRef.current) {
                  return
                }
                const willRetry = retryIdentityOnce()
                resetWidgetRuntime({ preserveOpenIntent: willRetry })
                setIdentity(null)
                setStatus(willRetry ? 'loading' : 'unavailable')
              })
          }}
        />
      ) : null}
    </OnetalkWidgetStatusProvider>
  )
}
